import { paymentCurrency } from './paymentCurrency';
import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import type { TippingComponentConfig } from '../types';
import { MIN_TIP_AMOUNT } from '../../utils/tipFee';

interface FeedbackTipAmountSelectorProps {
  amount?: number;
  amountLabel: string;
  config: TippingComponentConfig;
  customAmount?: string;
  customAmountBelowMinimum: boolean;
  onChange: (nextValue: { amount?: number; customAmount?: string }) => void;
  customAmountAsOption?: boolean;
  hideCustomUntilSelected?: boolean;
}

const FeedbackTipAmountSelector: React.FC<FeedbackTipAmountSelectorProps> = ({
  amount,
  amountLabel,
  config,
  customAmount,
  customAmountBelowMinimum,
  onChange,
  customAmountAsOption = false,
  hideCustomUntilSelected = false,
}) => {
  const isVertical = config.layout === 'vertical';
  const style = config.style ?? 'tiles';
  const buttonHeight = style === 'tiles' ? 70 : 48;
  const buttonRadius = style === 'pills' ? '999px' : '6px';
  const buttonFontSize = style === 'tiles' ? 20 : style === 'pills' ? 16 : 14;
  const columnCount = Math.max(1, Math.min(config.amounts.length, 3));
  const otherSelected = customAmountAsOption && !hideCustomUntilSelected && amount === undefined;
  const customAmountInputRef = React.useRef<HTMLInputElement | null>(null);
  const previouslyHidden = React.useRef(hideCustomUntilSelected);

  React.useEffect(() => {
    if (previouslyHidden.current && !hideCustomUntilSelected && amount === undefined) {
      customAmountInputRef.current?.focus();
    }
    previouslyHidden.current = hideCustomUntilSelected;
  }, [amount, hideCustomUntilSelected]);

  return (
    <Stack spacing={2} useFlexGap>
      <Typography
        component="span"
        sx={{
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: '1px',
          overflow: 'hidden',
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        }}
      >
        {config.paymentCopy?.['payment.selectedAmount'] ?? 'Selected tip amount:'} {amountLabel}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: style === 'compact' ? 0.75 : 1,
          gridTemplateColumns: isVertical
            ? 'minmax(0, 1fr)'
            : `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {config.amounts.map((tipAmount) => {
          const selected = amount === tipAmount.value && !customAmount;

          return (
            <Button
              aria-pressed={selected}
              disabled={
                !Number.isSafeInteger(Math.round(tipAmount.value * 100)) ||
                Math.abs(tipAmount.value * 100 - Math.round(tipAmount.value * 100)) >= 1e-7 ||
                tipAmount.value < Math.max(1, config.minimumAmount ?? 1) ||
                tipAmount.value > (config.maximumAmount ?? Number.MAX_SAFE_INTEGER)
              }
              key={tipAmount.value}
              onClick={() => onChange({ amount: tipAmount.value, customAmount: '' })}
              style={
                {
                  '--cx-tip-amount-bg': selected
                    ? config.selectedBackgroundColor || 'var(--cx-brand-button-selected-bg)'
                    : config.buttonBackgroundColor || 'var(--cx-brand-button-bg)',
                  '--cx-tip-amount-border': selected
                    ? config.selectedBorderColor || 'var(--cx-brand-button-selected-border)'
                    : config.buttonBorderColor || 'var(--cx-brand-button-border)',
                  '--cx-tip-amount-color': selected
                    ? config.selectedTextColor || 'var(--cx-brand-button-selected-text)'
                    : config.buttonTextColor || 'var(--cx-brand-button-text)',
                  borderRadius: buttonRadius,
                  minHeight: `${buttonHeight}px`,
                } as React.CSSProperties
              }
              sx={{
                bgcolor: 'var(--cx-tip-amount-bg)',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--cx-tip-amount-border)',
                color: 'var(--cx-tip-amount-color)',
                borderRadius: buttonRadius,
                fontSize: buttonFontSize,
                fontWeight: 600,
                height: buttonHeight,
                minHeight: buttonHeight,
                transition: selected ? 'none' : undefined,
                '&:hover': {
                  bgcolor: selected ? 'var(--cx-tip-amount-bg)' : 'var(--cx-brand-primary-soft)',
                  borderColor: 'var(--cx-tip-amount-border)',
                },
              }}
              type="button"
              variant="outlined"
            >
              {tipAmount.label}
            </Button>
          );
        })}
        {config.allowCustomAmount !== false && customAmountAsOption ? (
          <Button
            aria-pressed={otherSelected}
            onClick={() => onChange({ amount: undefined, customAmount: '' })}
            style={
              {
                '--cx-tip-amount-bg': otherSelected
                  ? config.selectedBackgroundColor || 'var(--cx-brand-button-selected-bg)'
                  : config.buttonBackgroundColor || 'var(--cx-brand-button-bg)',
                '--cx-tip-amount-border': otherSelected
                  ? config.selectedBorderColor || 'var(--cx-brand-button-selected-border)'
                  : config.buttonBorderColor || 'var(--cx-brand-button-border)',
                '--cx-tip-amount-color': otherSelected
                  ? config.selectedTextColor || 'var(--cx-brand-button-selected-text)'
                  : config.buttonTextColor || 'var(--cx-brand-button-text)',
                borderRadius: buttonRadius,
                minHeight: `${buttonHeight}px`,
              } as React.CSSProperties
            }
            sx={{
              bgcolor: 'var(--cx-tip-amount-bg)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--cx-tip-amount-border)',
              color: 'var(--cx-tip-amount-color)',
              borderRadius: buttonRadius,
              fontSize: buttonFontSize,
              fontWeight: 600,
              gridColumn: isVertical ? undefined : '1 / -1',
              height: buttonHeight,
              minHeight: buttonHeight,
              transition: otherSelected ? 'none' : undefined,
              '&:hover': {
                bgcolor: otherSelected ? 'var(--cx-tip-amount-bg)' : 'var(--cx-brand-primary-soft)',
                borderColor: 'var(--cx-tip-amount-border)',
              },
            }}
            type="button"
            variant="outlined"
          >
            {config.paymentCopy?.['payment.other'] ?? 'Other'}
          </Button>
        ) : null}
      </Box>

      {config.allowCustomAmount !== false && (!hideCustomUntilSelected || amount !== undefined) ? (
        <>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'var(--cx-brand-border)', flex: 1, height: '1px' }} />
            <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)' }}>
              {config.paymentCopy?.['payment.or'] ?? 'or'}
            </Typography>
            <Box sx={{ bgcolor: 'var(--cx-brand-border)', flex: 1, height: '1px' }} />
          </Box>
          <TextField
            fullWidth
            inputRef={customAmountInputRef}
            error={customAmountBelowMinimum}
            helperText={
              customAmountBelowMinimum
                ? (
                    config.paymentCopy?.['payment.range'] ??
                    'Enter an amount between {minimum} and {maximum}.'
                  )
                    .replace(
                      '{minimum}',
                      paymentCurrency(config.minimumAmount ?? 1, config.paymentLocale, true)
                    )
                    .replace(
                      '{maximum}',
                      config.maximumAmount === undefined
                        ? (config.paymentCopy?.['payment.maximum'] ?? 'the supported maximum')
                        : paymentCurrency(config.maximumAmount, config.paymentLocale, true)
                    )
                : undefined
            }
            onChange={(event) => onChange({ amount: undefined, customAmount: event.target.value })}
            placeholder={config.paymentCopy?.['payment.enterAmount'] ?? 'Enter amount'}
            sx={{
              '&& .MuiInputBase-input': { fontSize: 16, lineHeight: '24px' },
              '& .MuiInputBase-root': {
                bgcolor: 'var(--cx-brand-surface)',
                color: 'var(--cx-brand-text)',
                minHeight: 58,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--cx-brand-border)',
              },
            }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { color: 'var(--cx-brand-text-muted)' } }}>$</InputAdornment>,
              },
              htmlInput: {
                'aria-label': config.paymentCopy?.['payment.otherAmount'] ?? 'Other tip amount',
                inputMode: 'decimal',
                min: config.minimumAmount ?? MIN_TIP_AMOUNT,
                max: config.maximumAmount,
                step: '0.01',
              },
            }}
            type="number"
            value={customAmount ?? ''}
          />
        </>
      ) : null}
    </Stack>
  );
};

export default FeedbackTipAmountSelector;
